import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { VAPID_PUBLIC_KEY } from '../../graphql/queries';
import { REGISTER_PUSH_SUBSCRIPTION, UNREGISTER_PUSH_SUBSCRIPTION } from '../../graphql/mutations';
import Switch from '../Switch/Switch';
import {
  isPushSupported,
  getCurrentPushSubscription,
  enablePushNotifications
} from '../../utils/pushNotifications';
import '../../pages/AdminPanel/MateManagement/MateDetail/MateDetail.css';
import './PushNotificationsSettings.css';

export default function PushNotificationsSettings() {
  const [subscribed, setSubscribed] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [busy, setBusy] = useState(false);

  const permissionDenied =
    typeof Notification !== 'undefined' && Notification.permission === 'denied';

  const { data: vapidData, loading: vapidLoading } = useQuery(VAPID_PUBLIC_KEY, {
    fetchPolicy: 'network-only'
  });

  const vapidKey = vapidData?.vapidPublicKey;

  const [registerPush] = useMutation(REGISTER_PUSH_SUBSCRIPTION);
  const [unregisterPush] = useMutation(UNREGISTER_PUSH_SUBSCRIPTION);

  const refreshLocalState = useCallback(async () => {
    if (!isPushSupported()) {
      setSubscribed(false);
      return;
    }
    try {
      const sub = await getCurrentPushSubscription();
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
  }, []);

  useEffect(() => {
    refreshLocalState();
  }, [refreshLocalState]);

  const handleEnable = async () => {
    if (!vapidKey) {
      setStatusText('Det virker ikke endnu, kontakt administrator.');
      return;
    }
    if (!isPushSupported()) {
      setStatusText('Din browser understøtter ikke push-notifikationer.');
      return;
    }

    setBusy(true);
    setStatusText('');
    try {
      const json = await enablePushNotifications(vapidKey);
      if (!json) {
        setStatusText('Tilladelse til notifikationer blev ikke givet.');
        return;
      }

      await registerPush({
        variables: {
          subscription: {
            endpoint: json.endpoint,
            expirationTime:
              json.expirationTime != null ? String(json.expirationTime) : null,
            keys: {
              p256dh: json.keys.p256dh,
              auth: json.keys.auth
            }
          }
        }
      });

      setSubscribed(true);
      setStatusText('Notifikationer er slået til for denne enhed.');
    } catch (e) {
      setStatusText(e.message || 'Kunne ikke aktivere notifikationer. Genindlæs siden og prøv igen.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    setStatusText('');
    try {
      const sub = await getCurrentPushSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await unregisterPush({ variables: { endpoint } });
      }
      setSubscribed(false);
      setStatusText('Notifikationer er slået fra.');
    } catch (e) {
      setStatusText(e.message || 'Kunne ikke afmelde.');
    } finally {
      setBusy(false);
    }
  };

  const handleSwitchChange = async (checked) => {
    if (busy || vapidLoading) return;
    if (checked) {
      await handleEnable();
    } else {
      await handleDisable();
    }
  };

  if (!isPushSupported()) {
    return (
      <p className="push-settings__hint">
        Din browser understøtter ikke web-notifikationer. Brug fx Chrome, Edge eller Safari på iOS (tilføjet til hjemmeskærm).
      </p>
    );
  }

  const switchDisabled =
    busy || vapidLoading || !vapidKey || permissionDenied;

  return (
    <div className="push-settings">
      {vapidLoading && <p className="push-settings__hint">Henter indstillinger…</p>}

      {!vapidLoading && !vapidKey && (
        <p className="push-settings__hint push-settings__hint--warn">
          Det virker ikke endnu, kontakt administrator.
        </p>
      )}

      {permissionDenied && (
        <p className="push-settings__hint push-settings__hint--warn">
          Notifikationer er blokeret i browserindstillingerne. Tillad beskeder for dette site for at bruge funktionen.
        </p>
      )}

      {statusText && <p className="push-settings__status">{statusText}</p>}

      <p className="push-settings__hint push-settings__hint--intro">
        Modtag beskeder på låseskærmen, når der er nyt i Svirr.
      </p>

      <div className="mate-detail-form mate-detail-form-row push-settings__form">
        <div className="mate-detail-form-row-item">
          <label>Notifikationer på denne enhed</label>
          <span className="mate-detail-value push-settings__switch-wrap">
            <Switch
              checked={subscribed}
              onChange={handleSwitchChange}
              disabled={switchDisabled}
              ariaLabel="Slå push-notifikationer til eller fra"
              color="primary"
            />
            {busy && <span className="push-settings__busy">Opdaterer…</span>}
          </span>
        </div>
      </div>
    </div>
  );
}
